import { ADRESS_STATUS_KEYS } from './adressStatusLabels';

/**
 * 'Zelf op te pakken' — de ene plek die weet wat dat vinkje betekent.
 *
 * De planner zet het aan op een huishouden dat een klusser zonder overleg mag
 * oppakken. Het is een **eigenschap van de klus en geen aanbod**: het blijft
 * staan zodra iemand het oppakt, en daarom valt het huishouden vanzelf terug in
 * de lijst als die toewijzing later vervalt — bij een vervolgopdracht hoeft de
 * planner niets opnieuw te doen.
 *
 * Dat is bewust niet als status gebouwd. De statuslijst is gedeeld over alle
 * organisaties en staat in trechtervolgorde (ADR-032), en 'zelf op te pakken'
 * is geen fase: een huishouden dat zo gemarkeerd is, staat tegelijk gewoon op
 * 'Inplannen'. Eén veld met twee vragen erin verliest er altijd één — en de
 * automaat achter 'Automatisch Ingepland' schrijft al in `Status`.
 *
 * Omdat het vinkje blijft staan, is 'staat het in de lijst?' een andere vraag
 * dan 'staat het vinkje aan?'. Die bestaat uit DRIE feiten, en `isInPickupPool`
 * is de enige plek waar ze bij elkaar staan — elk scherm dat de lijst toont of
 * telt roept deze functie aan.
 * AI-CONTEXT: See docs/pitfalls.md#dev-second-source-never-reaches-its-own-gate
 * AI-CONTEXT: See docs/features/huishouden-zelf-op-te-pakken.md
 */

/**
 * De statussen waarin het traject nog loopt, afgeleid uit de trechtervolgorde.
 *
 * Niet als eigen lijst opgeschreven: die raakt achter zodra er een fase
 * bijkomt. Precies dat ging eerder mis bij `shouldAutoMarkPlanned`, waar
 * 'In afwachting' tussen twee statussen werd geschoven en een hardgecodeerde
 * lijst stil de verkeerde huishoudens overhield.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * @type {Array<string>}
 */
const PICKUP_OPEN_STATUSES = ADRESS_STATUS_KEYS
    .slice(0, ADRESS_STATUS_KEYS.indexOf('completed'));

/**
 * `ToggleButton` schrijft de strings `'true'`/`'false'`, een document dat het
 * vinkje nooit gezien heeft geeft `undefined`, en een handmatige correctie in
 * de Console kan een echte boolean achterlaten.
 * AI-CONTEXT: See docs/pitfalls.md#ui-togglebutton-string-only-branches
 *
 * @param {object|null|undefined} adress - Document uit Adresses
 * @returns {boolean}
 */
const isSelfPickup = (adress) => {
    if (!adress || typeof adress !== 'object') return false;
    const value = adress.SelfPickup;
    return value === true || value === 'true';
};

/**
 * Of het traject van dit huishouden nog loopt.
 *
 * Een status die we niet kennen telt als gesloten. Dit is een poort naar de
 * telefoon van een klusser, en 'weet ik niet' hoort daar dicht te vallen in
 * plaats van open — net als bij `shouldAutoMarkPlanned`, dat een onbekende
 * status met rust laat.
 * AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
 *
 * @param {string|null|undefined} status - Adresses.Status
 * @returns {boolean}
 */
const isPickupOpenStatus = (status) => PICKUP_OPEN_STATUSES.includes(status);

/**
 * Of er nog niemand aan dit huishouden werkt.
 *
 * `AssignedTo` is een array (meer dan één klusser mag erop staan) en ontbreekt
 * op huishoudens van vóór die kolom.
 *
 * @param {object} adress - Document uit Adresses
 * @returns {boolean}
 */
const hasNobodyAssigned = (adress) => {
    const assigned = adress.AssignedTo;
    return !Array.isArray(assigned) || assigned.length === 0;
};

/**
 * Of dit huishouden in de lijst 'Zelf op te pakken' hoort te staan.
 *
 * Drie feiten, en ze zijn geen van drieën gemist:
 * - het vinkje staat aan — de planner vindt dit geschikt;
 * - er is niemand toegewezen — er is nog niemand mee bezig, en zonder dit
 *   rijden twee klussers naar dezelfde deur;
 * - het traject loopt nog — anders komt een afgerond huishouden terug in de
 *   lijst zodra iemand de toewijzing opruimt. Dat is het enige scenario waarin
 *   een vinkje dat blijft staan echt misgaat.
 *
 * @param {object|null|undefined} adress - Document uit Adresses
 * @returns {boolean}
 */
const isInPickupPool = (adress) => {
    if (!isSelfPickup(adress)) return false;
    if (!hasNobodyAssigned(adress)) return false;
    return isPickupOpenStatus(adress.Status);
};

/**
 * Of deze klusser het vinkje zelf mag uitzetten.
 *
 * Hij mag dat alleen op een huishouden dat hij al heeft opgepakt. Dat is ook
 * het moment waarop hij het ontdekt: hij is er geweest en het bleek geen klein
 * klusje. Zou hij het kunnen uitzetten terwijl het nog in de lijst staat, dan
 * verdween het stil bij iedereen terwijl er niemand aan werkt — en de planner
 * ziet daar niets van. Nu staat er in haar overzicht 'toegewezen aan Cordo,
 * vinkje uit', en dat vertelt het hele verhaal.
 *
 * De correctie verandert niets aan nu (hij staat erop, dus het huishouden is al
 * uit de lijst); ze werkt pas als de toewijzing vervalt.
 *
 * @param {object|null|undefined} adress - Document uit Adresses
 * @param {string|null|undefined} personaId - Personas.ID van de klusser
 * @returns {boolean}
 */
const mayClearSelfPickup = (adress, personaId) => {
    if (!isSelfPickup(adress)) return false;
    if (typeof personaId !== 'string' || personaId === '') return false;
    const assigned = adress.AssignedTo;
    return Array.isArray(assigned) && assigned.includes(personaId);
};

export {
    PICKUP_OPEN_STATUSES,
    isSelfPickup,
    isPickupOpenStatus,
    isInPickupPool,
    mayClearSelfPickup,
};
